import React from "react";
import { Navigate, useRoutes } from "react-router-dom";
import Layout from "./layouts/Layout";
import AuthLayout from "./layouts/AuthLayout";
import Redirect from "./pages/Redirect";
import Login from "./pages/Auth/Login";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import ResetPassword from "./pages/Auth/ResetPassword";

// 既有模組（照設計稿重做）
import DashboardOverview from "./pages/Dashboard/Overview";
import MyTaskIndex from "./pages/MyTask/Index";
import ProjectList from "./pages/Project/List";
import ProjectDetail from "./pages/Project/Detail";
import ProjectAdd from "./pages/Project/Add";
import ScheduleCalendar from "./pages/Schedule/Calendar";
import PerformanceTeam from "./pages/Performance/Team";
import PerformanceReport from "./pages/Performance/Report";
import SettingRole from "./pages/Setting/Role";
import SettingRoles from "./pages/Setting/Roles";
import ScreenMap from "./pages/Map/Index";
import SettingAdmin from "./pages/Setting/Admin";
import SettingHoliday from "./pages/Setting/Holiday";
import SettingPreferences from "./pages/Setting/Preferences";

// 新模組
import Approvals from "./pages/Approval/Index";
import ApprovalView from "./pages/Approval/View";
import Board from "./pages/Board/Index";
import Reports from "./pages/Report/Index";
import CodeScan from "./pages/CodeScan/Index";
import VulnScan from "./pages/VulnScan/Index";
import Timesheet from "./pages/Timesheet/Index";
import Audit from "./pages/Audit/Index";
import Automation from "./pages/Automation/Index";

/*
 * 改版新畫面（目前多數還是佔位殼）。
 *
 * 先把網址接出來，是因為後面每個畫面工項都要能單獨驗收 —— 沒有可抵達的網址，
 * 實作的人得先自己改路由，改完彼此又會衝突。舊的 24 條 route 一條都沒動，
 * 直接打舊網址仍然進得去。
 */
import TaskNew from "./pages/Task/New";
import TaskDetail from "./pages/Task/Detail";
import ScheduleGoals from "./pages/Schedule/Goals";
import ScheduleWeek from "./pages/Schedule/Week";
import ScheduleGoalNew from "./pages/Schedule/GoalNew";
import ScheduleGoalPlan from "./pages/Schedule/GoalPlan";
import ScanIndex from "./pages/Scan/Index";
import NotifyIndex from "./pages/Notify/Index";
import SearchIndex from "./pages/Search/Index";
import MeIndex from "./pages/Me/Index";
import MePassword from "./pages/Me/Password";
import GuideIndex from "./pages/Guide/Index";
import SettingAdminEdit from "./pages/Setting/AdminEdit";
import ProjectImport from "./pages/Project/Import";

const App = () => {
  const caseSensitive = true;
  return useRoutes([
    {
      path: "/Auth",
      caseSensitive,
      element: <AuthLayout />,
      children: [
        { path: "/Auth/Login", element: <Login />, caseSensitive },
        { path: "/Auth/ForgotPassword", element: <ForgotPassword />, caseSensitive },
        { path: "/Auth/ResetPassword", element: <ResetPassword />, caseSensitive },
      ],
    },
    {
      path: "/",
      element: <Layout />,
      children: [
        { path: "/", element: <Navigate to="/Redirect" replace /> },
        { path: "/Redirect", element: <Redirect />, caseSensitive },

        { path: "/Dashboard", element: <DashboardOverview />, caseSensitive },
        { path: "/MyTask", element: <MyTaskIndex />, caseSensitive },
        /*
         * 畫面索引。設計稿把它當成一個正式畫面（頁首右上角有入口），
         * 用途是讓沒跟到改版的人自己點一遍，看舊畫面現在跑到哪裡去了。
         * 遷移計畫整個漏掉了這一頁，補回來。
         */
        { path: "/Map", element: <ScreenMap />, caseSensitive },
        { path: "/Approvals", element: <Approvals />, caseSensitive },
        /* 審核畫面。計畫漏了這一條，沒有它 pages/Approval/View.js 永遠進不去 */
        { path: "/Approvals/:taskId", element: <ApprovalView />, caseSensitive },

        { path: "/Project", element: <Navigate to="/Project/List" replace /> },
        { path: "/Project/List", element: <ProjectList />, caseSensitive },
        { path: "/Project/Add", element: <ProjectAdd />, caseSensitive },
        { path: "/Project/Detail/:id", element: <ProjectDetail />, caseSensitive },

        // 「時程」現在的入口是里程碑頁，不再轉去看板（看板改掛在「專案」底下）
        { path: "/Schedule", element: <ScheduleGoals />, caseSensitive },
        { path: "/Board", element: <Board />, caseSensitive },
        { path: "/Schedule/Calendar", element: <ScheduleCalendar />, caseSensitive },

        { path: "/Performance", element: <Navigate to="/Performance/Team" replace /> },
        { path: "/Performance/Team", element: <PerformanceTeam />, caseSensitive },
        { path: "/Performance/Report", element: <PerformanceReport />, caseSensitive },
        { path: "/Reports", element: <Reports />, caseSensitive },
        { path: "/CodeScan", element: <CodeScan />, caseSensitive },
        { path: "/VulnScan", element: <VulnScan />, caseSensitive },
        { path: "/Timesheet", element: <Timesheet />, caseSensitive },
        { path: "/Audit", element: <Audit />, caseSensitive },

        // 設定的著陸頁改成偏好設定：角色與權限要 role 權限，一般人打 /Setting 會直接撞 NoPermission
        { path: "/Setting", element: <Navigate to="/Setting/Preferences" replace /> },
        /*
         * /Setting/Roles 是唯讀的角色說明頁；可編輯的權限矩陣在 /Setting/Roles/Matrix。
         * 兩條都指向 Setting/Role.js 的話，設計稿要的說明頁等於不存在，
         * 而且同一份危險操作會有兩個入口。
         */
        { path: "/Setting/Roles", element: <SettingRoles />, caseSensitive },
        { path: "/Setting/Admin", element: <SettingAdmin />, caseSensitive },
        { path: "/Automation", element: <Automation />, caseSensitive },
        { path: "/Setting/Holiday", element: <SettingHoliday />, caseSensitive },
        { path: "/Setting/Preferences", element: <SettingPreferences />, caseSensitive },

        /* ---------- 改版新增的網址 ---------- */
        /* 「今天」已併入個人工項，舊網址與書籤導過去 */
        { path: "/Today", element: <Navigate to="/MyTask" replace />, caseSensitive },
        // 跨專案工項清單；舊的 /Board 與 /MyTask 不受影響
        /* 獨立的工項清單拿掉了；舊書籤與舊連結導到個人工項，不要變成 404 */
        { path: "/Tasks", element: <Navigate to="/MyTask" replace />, caseSensitive },
        // 靜態片段 New 的排名高於動態 :id，react-router v6 自己會挑對，不必靠陣列順序
        { path: "/Task/New", element: <TaskNew />, caseSensitive },
        { path: "/Task/:id", element: <TaskDetail />, caseSensitive },
        { path: "/Schedule/Week", element: <ScheduleWeek />, caseSensitive },
        { path: "/Schedule/Goal/New", element: <ScheduleGoalNew />, caseSensitive },
        { path: "/Schedule/Goal/Plan", element: <ScheduleGoalPlan />, caseSensitive },
        { path: "/Scan", element: <ScanIndex />, caseSensitive },
        { path: "/Notifications", element: <NotifyIndex />, caseSensitive },
        { path: "/Search", element: <SearchIndex />, caseSensitive },
        { path: "/Me", element: <MeIndex />, caseSensitive },
        { path: "/Me/Password", element: <MePassword />, caseSensitive },
        { path: "/Guide", element: <GuideIndex />, caseSensitive },
        // 角色權限表（矩陣）先沿用現有的角色頁；/Setting/Roles 之後改成唯讀的角色說明頁
        { path: "/Setting/Roles/Matrix", element: <SettingRole />, caseSensitive },
        // 新增與編輯人員共用同一支：差別只在有沒有帶 account
        { path: "/Setting/Admin/New", element: <SettingAdminEdit />, caseSensitive },
        { path: "/Setting/Admin/:account", element: <SettingAdminEdit />, caseSensitive },
        { path: "/Project/Import/:id", element: <ProjectImport />, caseSensitive },
      ],
    },
  ]);
};

export default App;
